import { useCallback, useEffect, useState, useRef } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { useContracts } from './useContract';

const BN0 = ethers.BigNumber.from(0);

export function useUserData() {
  const { account, isCorrectNetwork } = useWallet();
  const { read } = useContracts();

  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(0);
  const [user, setUser] = useState(null);
  const [packages, setPackages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [totals, setTotals] = useState({
    activePackageCount: BN0,
    totalMiningEarned: BN0,       // lifetime mining
    totalLevelIncomeEarned: BN0,  // lifetime level rewards
  });
  const [balances, setBalances] = useState({
    usdt: BN0,
    xt: BN0,
    allowance: BN0,
  });

  const versionRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!account) {
      setUserId(0);
      setUser(null);
      setPackages([]);
      setActivities([]);
      return;
    }
    const version = ++versionRef.current;
    setLoading(true);

    // ---- Parallel reads ----
    const idPromise = read.logic.addressToId(account);
    const balancesPromise = Promise.all([
      read.usdt.balanceOf(account),
      read.xt.balanceOf(account),
      // Approval target is the Xterra contract itself
      read.usdt.allowance(account, read.logic.address),
    ]);

    balancesPromise
      .then(([usdtBal, xtBal, allowance]) => {
        if (version !== versionRef.current) return;
        setBalances({ usdt: usdtBal, xt: xtBal, allowance });
      })
      .catch(() => {});

    try {
      const idBn = await idPromise;
      const id = idBn.toNumber();

      if (version !== versionRef.current) return;
      setUserId(id);

      if (id === 0) {
        setUser(null);
        setPackages([]);
        setActivities([]);
      } else {
        const [uRes, pRes, incRes, actsRes, headRes] = await Promise.allSettled([
          read.logic.getUser(id),
          read.logic.getAllPackages(id),
          read.logic.getIncomeBreakdown(id),
          read.logic.getActivities(id),
          read.logic.activityHead(id),
        ]);

        if (version !== versionRef.current) return;

        if (uRes.status === 'fulfilled') setUser(uRes.value);

        if (pRes.status === 'fulfilled') {
          const pkgs = pRes.value;
          setPackages(pkgs);
          const activeCount = pkgs.reduce((n, p) => n + (p.active ? 1 : 0), 0);
          setTotals((t) => ({ ...t, activePackageCount: ethers.BigNumber.from(activeCount) }));
        }

        if (incRes.status === 'fulfilled') {
          setTotals((t) => ({
            ...t,
            totalMiningEarned: incRes.value.lifetimeMining,
            totalLevelIncomeEarned: incRes.value.lifetimeLevelIncome,
          }));
        }

        if (actsRes.status === 'fulfilled' && headRes.status === 'fulfilled') {
          const acts = actsRes.value;
          const head = headRes.value;
          const ordered = [];
          const size = acts.length;
          for (let i = 0; i < size; i++) {
            const idx = (Number(head) - 1 - i + size) % size;
            const a = acts[idx];
            if (a && Number(a.timestamp) > 0) ordered.push(a);
          }
          setActivities(ordered);
        }
      }
    } catch (err) {
      // silent read failure
    } finally {
      if (version === versionRef.current) setLoading(false);
    }
  }, [account, read]);

  useEffect(() => {
    if (account && isCorrectNetwork) {
      refresh();
    } else {
      setUserId(0);
      setUser(null);
      setPackages([]);
      setActivities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isCorrectNetwork]);

  return {
    loading,
    userId,
    user,
    packages,
    activities,
    totals,
    balances,
    refresh,
    isRegistered: userId > 0,
  };
}
